// MatchCard — live standings for a Match Play (match_1v1) side game.
//
// Two sides (best-ball per team). Head-to-head hole-by-hole. Closes
// out when lead exceeds remaining. Status labels follow standard
// match-play convention: "3 & 2" (closed out), "2 up thru 7",
// "AS", "Dormie".
//
// Read-only display of get_match_state:
//   • Big status banner up top with the running match state.
//   • Side A / Side B roster cards with units_won display.
//   • Per-hole result chip strip (winner colored).

import { useCallback, useEffect, useState } from 'react'
import { db } from '../lib/db'

export default function MatchCard({ game_id, autoRefreshMs = 0 }) {
  const [state, setState] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const { data } = await db('get_match_state', { game_id })
      setState(data)
      setError(data?.error || null)
    } catch (e) {
      setError(e.message || 'Match compute failed')
    }
    if (!silent) setLoading(false)
  }, [game_id])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (!autoRefreshMs) return
    const t = setInterval(() => load({ silent: true }), autoRefreshMs)
    return () => clearInterval(t)
  }, [autoRefreshMs, load])

  if (loading && !state) return <p className="text-muted text-sm">Loading Match…</p>
  if (error && !state) return <p style={{ color: 'var(--red)' }}>{error}</p>
  if (!state) return null

  const { participants = [], sides, holes = [], totals = {}, winner_side, closed_out_on_hole, final_state, total_holes } = state
  const nameFor = (pid) => participants.find(p => p.player_id === pid)?.name || pid.slice(0, 6)

  const status = describeMatch({ holes, winner_side, closed_out_on_hole, final_state, total_holes })

  return (
    <div>
      <div style={{
        marginBottom: 12,
        padding: '14px 12px', borderRadius: 8,
        background: winner_side
          ? winner_side === 'A' ? 'rgba(212,185,122,0.15)' : 'rgba(125,211,168,0.12)'
          : 'var(--green-dark)',
        border: `1px solid ${
          winner_side === 'A' ? 'var(--gold)' :
          winner_side === 'B' ? 'var(--green-mid)' :
          'var(--green-mid)'
        }`,
        textAlign: 'center',
      }}>
        <p style={{
          margin: 0, fontSize: '1.05rem', fontWeight: 700,
          color: winner_side ? (winner_side === 'A' ? 'var(--gold)' : 'var(--cream)') : 'var(--cream)',
          letterSpacing: '0.02em',
        }}>
          {status.headline}
        </p>
        {status.sub && (
          <p style={{ margin: '3px 0 0', fontSize: '0.75rem', color: 'var(--gray-300)' }}>
            {status.sub}
          </p>
        )}
      </div>

      {sides && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
          <SideBadge side="A" ids={sides.A || []} nameFor={nameFor} totals={totals} winner={winner_side === 'A'} />
          <SideBadge side="B" ids={sides.B || []} nameFor={nameFor} totals={totals} winner={winner_side === 'B'} />
        </div>
      )}

      {holes.length > 0 && (
        <div>
          <p style={{ fontSize: '0.7rem', color: 'var(--gray-300)', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Hole-by-hole
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 3 }}>
            {holes.map(h => {
              const bg = h.winner_side === 'A' ? 'var(--gold)'
                : h.winner_side === 'B' ? 'var(--green-mid)'
                : 'var(--green-deep)'
              const fg = h.winner_side === 'A' ? 'var(--green-deep)'
                : h.winner_side === 'B' ? 'var(--cream)'
                : 'var(--gray-400)'
              return (
                <div
                  key={h.hole}
                  title={`Hole ${h.hole}${h.par ? ` · par ${h.par}` : ''}: A ${h.team_scores.A} vs B ${h.team_scores.B}${h.winner_side ? ` (${h.winner_side})` : ' halve'} · state ${h.state_after > 0 ? `A ${h.state_after}↑` : h.state_after < 0 ? `B ${Math.abs(h.state_after)}↑` : 'AS'}`}
                  style={{
                    padding: '4px 0', borderRadius: 4,
                    background: bg, color: fg,
                    textAlign: 'center',
                    fontFamily: 'var(--font-mono)', fontSize: '0.68rem', fontWeight: 700,
                    border: '1px solid var(--green-mid)',
                  }}
                >
                  {h.hole}
                </div>
              )
            })}
          </div>
          <p style={{ fontSize: '0.65rem', color: 'var(--gray-500)', margin: '6px 0 0' }}>
            Gold = A wins the hole · dark = B wins · empty = halve
          </p>
        </div>
      )}
    </div>
  )
}

function SideBadge({ side, ids, nameFor, totals, winner }) {
  const units = ids.reduce((s, pid) => s + (Number(totals[pid]?.units) || 0), 0)
  const color = side === 'A' ? 'var(--gold)' : 'var(--green-mid)'
  const fg = side === 'A' ? 'var(--green-deep)' : 'var(--cream)'
  return (
    <div style={{
      padding: 10, borderRadius: 6,
      background: winner ? 'rgba(212,185,122,0.15)' : 'var(--green-dark)',
      border: `1px solid ${winner ? 'var(--gold)' : color}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{
          padding: '2px 8px', borderRadius: 999,
          background: color, color: fg,
          fontSize: '0.7rem', fontWeight: 700,
        }}>Side {side}{winner ? ' · winner' : ''}</span>
        {units !== 0 && (
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.78rem', fontWeight: 700,
            color: units > 0 ? 'var(--blue-birdie)' : 'var(--red)',
          }}>{units > 0 ? '+' : ''}{units.toFixed(2)}</span>
        )}
      </div>
      <div style={{ fontSize: '0.72rem', color: 'var(--cream)', lineHeight: 1.4 }}>
        {ids.map(pid => nameFor(pid)).join(', ') || '—'}
      </div>
    </div>
  )
}

// Standard match-play status labels.
function describeMatch({ holes, winner_side, closed_out_on_hole, final_state, total_holes }) {
  const played = holes.length
  if (played === 0) return { headline: '—', sub: 'No holes scored yet.' }
  if (winner_side && closed_out_on_hole) {
    const lead = Math.abs(final_state)
    const rem = total_holes - closed_out_on_hole
    return {
      headline: `Side ${winner_side} wins ${lead} & ${rem}`,
      sub: `Match closed out on hole ${closed_out_on_hole}.`,
    }
  }
  if (winner_side && !closed_out_on_hole) {
    const lead = Math.abs(final_state)
    return {
      headline: `Side ${winner_side} wins ${lead} up`,
      sub: `Final result over ${total_holes} holes.`,
    }
  }
  if (final_state === 0) {
    return { headline: `All square thru ${played}`, sub: null }
  }
  const leader = final_state > 0 ? 'A' : 'B'
  const lead = Math.abs(final_state)
  const remaining = total_holes - played
  if (lead === remaining) {
    return { headline: `Side ${leader} dormie`, sub: `${lead} up thru ${played}, ${remaining} to play.` }
  }
  return { headline: `Side ${leader} ${lead} up thru ${played}`, sub: `${remaining} hole${remaining === 1 ? '' : 's'} to play.` }
}
